import gql from 'graphql-tag';
import { apolloQuery, BaseModel, QueryResult } from 'vue-apollo-model';
import { Query } from '@/generated-models';
import { RouteName } from '../router';
import { transformText } from '@/common/util/parseText';
import { getCurrentPageName, ExpKey } from '@/common/utils';
const mediaType: Record<number, string> = {
    1: 'hls',
    2: 'mp4',
    3: 'flv',
};
const errorVideoText: Record<number, string> = {
    21: '当前作品已失效',
    208003: '找不到该作品，为你推荐了热门作品',
    208004: '找不到该作品，为你推荐了热门作品',
    208005: '暂未支持显示图片作品，请在移动端查看',
};
/**
 * video detail models 获取视频详情页信息
 */
export default class VideoDetailModel extends BaseModel {
    canRefetch = false;
    private photoId = '';
    @apolloQuery<VideoDetailModel>({
        query: gql`
            query visionVideoDetail($photoId: String, $type: String, $page: String, $webPageArea: String){
                visionVideoDetail(photoId: $photoId, type: $type, page: $page, webPageArea: $webPageArea){
                    status
                    type
                    author {
                        id
                        name
                        following
                        headerUrl
                    }
                    photo {
                        id
                        duration
                        caption
                        likeCount
                        realLikeCount
                        coverUrl
                        photoUrl
                        liked
                        timestamp
                        expTag
                        llsid
                        viewCount
                        videoRatio
                        stereoType
                        musicBlocked
                        manifest {
                            mediaType
                            businessType
                            version
                            adaptationSet {
                                id
                                duration
                                representation {
                                    id
                                    defaultSelect
                                    backupUrl
                                    codecs
                                    url
                                    height
                                    width
                                    avgBitrate
                                    maxBitrate
                                    m3u8Slice
                                    qualityType
                                    qualityLabel
                                    frameRate
                                    featureP2sp
                                    hidden
                                    disableAdaptive
                                }
                            }
                        }
                        manifestH265
                        photoH265Url
                        coronaCropManifest
                        coronaCropManifestH265
                        croppedPhotoH265Url
                        croppedPhotoUrl
                        videoResource
                    }
                    tags {
                        type
                        name
                    }
                    commentLimit {
                        canAddComment
                    }
                    llsid
                    danmakuSwitch
                }
            }
        `,
        variables($route) {
            const pageName = $route.query.page || $route.name;
            return {
                photoId: $route.params.photoId || this.photoId,
                page: getCurrentPageName(pageName as ExpKey),
                webPageArea: $route.query.area,
            };
        },
        prefetch: true,
        // fetchPolicy: 'cache-and-network',
        skip($router) {
            // 进入短视频页面不主动调用
            return ($router.name === RouteName.ShortVideo || $router.name === RouteName.NewReco) && !this.canRefetch;
        },
    })
    videoDetail!: QueryResult<{
        visionVideoDetail: Query['visionVideoDetail'];
    }>

    fetchData(photoId: string) {
        if (this.videoDetail.loading) {return;}
        this.photoId = photoId;
        this.videoDetail.fetchMore({
            updateQuery: (prev, next) => {
                this.canRefetch = false;
                return {
                    visionVideoDetail: {
                        __typename: prev.visionVideoDetail?.__typename || 'VisionVideoDetail',
                        ...(next?.visionVideoDetail || {}),
                    },
                };
            },
        });
    }

    get author() {
        return this.videoDetail.data.visionVideoDetail?.author || {};
    }

    get photo() {
        return this.videoDetail.data.visionVideoDetail?.photo || {};
    }

    get hasManifest() {
        return !!this.photo.manifest?.adaptationSet?.length || false;
    }

    get mediaType() {
        return this.hasManifest ? mediaType[this.photo?.manifest?.mediaType || 2] : mediaType[2];
    }

    get supDanmaku() {
        // @ts-ignore
        return this.videoDetail.data.visionVideoDetail?.danmakuSwitch;
    }

    get playInfo() {
        const { $route, photo } = this;
        // 当是短视频页面时，播放数据直接取 videoResource，不包含三段式逻辑
        if ($route.name === RouteName.ShortVideo) {
            return photo.videoResource;
        }

        // 三段式资源
        const croppedH264 = photo.coronaCropManifest || photo.croppedPhotoUrl;
        const croppedH265 = photo.coronaCropManifestH265 || photo.croppedPhotoH265Url;

        if (croppedH264 && !croppedH265) {
            return {
                h264: croppedH264,
            };
        }
        return {
            h264: croppedH264 || photo.manifest || photo.photoUrl,
            hevc: croppedH265 || photo.manifestH265 || photo.photoH265Url,
        };
    }

    get videoPhotoCaption() {
        const photo = this.videoDetail.data.visionVideoDetail?.photo;
        if (photo) {
            return transformText(photo?.caption as string);
        }
        return '';
    }

    get videoTags() {
        return this.videoDetail.data.visionVideoDetail?.tags || [];
    }

    get canAddComment() {
        return this.videoDetail.data.visionVideoDetail?.commentLimit?.canAddComment || 0;
    }

    get status() {
        return this.videoDetail.data.visionVideoDetail?.status;
    }

    get errorVideoText() {
        if (typeof this.status === 'number') {
            if (this.status !== 1) {
                return errorVideoText[this.status] || '当前作品已失效';
            }
            return '';
        }
        return '当前作品已失效';
    }

    get type() {
        return this.videoDetail.data.visionVideoDetail?.type;
    }

    get llsid() {
        return this.videoDetail.data.visionVideoDetail?.llsid;
    }
}
